import * as React from "react"
import { Button, buttonVariants } from "./button"
import { Loader2, Check, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "sonner"

const AsyncButton = React.forwardRef(({
  className,
  variant,
  size,
  children,
  onClick,
  successMessage,
  errorMessage,
  loadingText,
  successDuration = 1500,
  disabled,
  ...props
}, ref) => {
  const [state, setState] = React.useState('idle')

  const handleClick = async (e) => {
    if (state !== 'idle' || disabled) return

    setState('loading')
    try {
      await onClick?.(e)
      setState('success')
      if (successMessage) {
        toast.success(successMessage)
      }
      setTimeout(() => setState('idle'), successDuration)
    } catch (error) {
      setState('error')
      const message = errorMessage || error?.message || 'An error occurred'
      toast.error(message)
      setTimeout(() => setState('idle'), 2000)
    }
  }

  const isLoading = state === 'loading'
  const isSuccess = state === 'success'
  const isError = state === 'error'

  return (
    <Button
      ref={ref}
      className={cn(
        isSuccess && "bg-green-600 hover:bg-green-600",
        isError && "bg-red-600 hover:bg-red-600",
        className
      )}
      variant={variant}
      size={size}
      disabled={isLoading || disabled}
      onClick={handleClick}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
      {isSuccess && <Check className="w-4 h-4 mr-2" aria-hidden="true" />}
      {isError && <AlertCircle className="w-4 h-4 mr-2" aria-hidden="true" />}
      {isLoading ? (loadingText || children) : children}
    </Button>
  )
})

AsyncButton.displayName = "AsyncButton"

export { AsyncButton }
